import { AxiosResponse } from "axios";
import Axios from "../utils/http-request";
import { getAccessToken } from "../utils/storage";
import { RepresentativeApplyProps, UpdateProfileProps } from "../interface/api";
import { PostContentDetails } from "../interface/posts";
import { ReplyDataProps, ReportContainerPropsType } from "../interface/account";

export const apiGetNotification = async () => {
  const { accessToken, tokenType } = await getAccessToken();
  try {
    const uri = `/accounts/notifications`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the GET request
    const result = await Axios.get(uri, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.details || e?.details,
    };
  }
};
export const apiGetAccountProfile = async () => {
  const { accessToken, tokenType } = await getAccessToken();
  try {
    const uri = `/accounts/profile`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the GET request
    const result = await Axios.get(uri, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.details || e?.details,
    };
  }
};

export const apiGetAccount = async (account_id: number) => {
  const id = !!account_id ? account_id : 0;
  const { accessToken, tokenType } = await getAccessToken();

  try {
    const uri = `/accounts/${id}`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the GET request
    const result = await Axios.get(uri, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.details || e?.details,
    };
  }
};

export const apiCheckAccountStatus = async () => {
  const { accessToken, tokenType } = await getAccessToken();

  try {
    const uri = `/accounts/status`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the GET request
    const result = await Axios.get(uri, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: 500,
      details: e?.response?.data?.details || e?.details,
    };
  }
};
export const apiUploadProfilePhoto = async (photo: File) => {
  const { accessToken, tokenType } = await getAccessToken();

  try {
    const uri = `/accounts/profile/upload/profile`;
    const formData = new FormData();
    formData.append("photo", photo);

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.details || e?.details,
    };
  }
};

export const apiUploadCoverPhoto = async (photo: File) => {
  const { accessToken, tokenType } = await getAccessToken();
  try {
    const uri = `/accounts/profile/upload/cover`;
    const formData = new FormData();
    formData.append("photo", photo);

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.details || e?.details,
    };
  }
};

export const apiUpdateProfile = async (values: UpdateProfileProps) => {
  const { accessToken, tokenType } = await getAccessToken();

  try {
    const uri = `/accounts/profile/update`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };
    const formData = JSON.stringify(values);

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};

export const apiLogout = async () => {
  const { accessToken, tokenType } = await getAccessToken();
  try {
    const uri = `/auth/logout`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `${tokenType} ${accessToken}`,
      },
    };

    // Send the POST request
    const result = await Axios.post(uri, {}, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.details || e?.details,
    };
  }
};

export const apiApplyForResp = async (values: RepresentativeApplyProps) => {
  const { accessToken, tokenType } = await getAccessToken();
  try {
    const uri = `/accounts/representatives/apply`;
    const formData = new FormData();

    // Set up headers for the request
    const cfg = {
      headers: {
        Authorization: `${tokenType} ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    };

    // Append all form fields to FormData
    Object.entries(values).forEach(([key, value]) => {
      if (key === "proof_of_office") {
        // If files is an array
        if (Array.isArray(values?.proof_of_office)) {
          values?.proof_of_office.forEach((file) => {
            formData.append("proof_of_office[]", file);
          });
        }
      } else if (key === "social_handles") {
        if (Array.isArray(values?.social_handles)) {
          values?.social_handles.forEach((file) => {
            formData.append("social_handles[]", file);
          });
        }
      } else {
        formData.append(key, value.toString());
      }
    });

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error("Error details:", e.response?.data);
    return {
      data: {},
      status: "error",
      details: e.response?.data || "Server error",
    };
  }
};

//

interface MetaData {
  current_page: number;
  last_page: number;
}

interface APIResponse<T> {
  data: T[];
  meta: MetaData;
}

interface PaginationParams {
  page: number;
  page_size: number;
  account_id: number;
}

export const apiGetAccountPosts = async (
  pagination: PaginationParams
): Promise<{ data: APIResponse<PostContentDetails> }> => {
  const { accessToken, tokenType } = await getAccessToken();
  const uri = `/accounts/posts?account_id=${pagination.account_id}&page=${pagination.page}&page_size=${pagination.page_size}`;
  const cfg = {
    headers: {
      Authorization: `${tokenType} ${accessToken}`,
    },
  };

  try {
    const result = await Axios.get<APIResponse<PostContentDetails>>(uri, cfg);
    return { data: result.data }; // Successful response
  } catch (e: any) {
    console.error(e);
    // Return a consistent error shape
    return {
      data: {
        data: [],
        meta: { current_page: pagination.page, last_page: pagination.page },
      },
    };
  }
};

export const apiGetAccountPetitions = async (
  pagination: PaginationParams
): Promise<{ data: APIResponse<ReportContainerPropsType> }> => {
  const { accessToken, tokenType } = await getAccessToken();
  const uri = `/accounts/petitions?account_id=${pagination.account_id}&page=${pagination.page}&page_size=${pagination.page_size}`;
  const cfg = {
    headers: {
      Authorization: `${tokenType} ${accessToken}`,
    },
  };

  try {
    const result = await Axios.get<APIResponse<ReportContainerPropsType>>(
      uri,
      cfg
    );
    return { data: result.data }; // Successful response
  } catch (e: any) {
    console.error(e);
    // Return a consistent error shape
    return {
      data: {
        data: [],
        meta: { current_page: pagination.page, last_page: pagination.page },
      },
    };
  }
};

export const apiGetAccountBookmark = async (
  pagination: PaginationParams
): Promise<{ data: APIResponse<ReportContainerPropsType> }> => {
  const { accessToken, tokenType } = await getAccessToken();
  const uri = `/accounts/bookmarks?account_id=${pagination.account_id}&page=${pagination.page}&page_size=${pagination.page_size}`;
  const cfg = {
    headers: {
      Authorization: `${tokenType} ${accessToken}`,
    },
  };

  try {
    const result = await Axios.get<APIResponse<ReportContainerPropsType>>(
      uri,
      cfg
    );
    return { data: result.data }; // Successful response
  } catch (e: any) {
    console.error(e);
    // Return a consistent error shape
    return {
      data: {
        data: [],
        meta: { current_page: pagination.page, last_page: pagination.page },
      },
    };
  }
};

export const apiGetAccountReplies = async (
  pagination: PaginationParams
): Promise<{ data: APIResponse<ReplyDataProps> }> => {
  const { accessToken, tokenType } = await getAccessToken();
  const uri = `/accounts/replies?account_id=${pagination.account_id}&page=${pagination.page}&page_size=${pagination.page_size}`;
  const cfg = {
    headers: {
      Authorization: `${tokenType} ${accessToken}`,
    },
  };

  try {
    const result = await Axios.get<APIResponse<ReplyDataProps>>(
      uri,
      cfg
    );
    return { data: result.data }; // Successful response
  } catch (e: any) {
    console.error(e);
    // Return a consistent error shape
    return {
      data: {
        data: [],
        meta: { current_page: pagination.page, last_page: pagination.page },
      },
    };
  }
};


export const apiGetRepTaggedPetitions = async (
  pagination: { page: number, page_size: number}
): Promise<{ data: APIResponse<ReportContainerPropsType> }> => {
  const { accessToken, tokenType } = await getAccessToken();
  const uri = `/accounts/petitions/tagged?page=${pagination.page}&page_size=${pagination.page_size}`;
  const cfg = {
    headers: {
      Authorization: `${tokenType} ${accessToken}`,
    },
  };

  try {
    const result = await Axios.get<APIResponse<ReportContainerPropsType>>(
      uri,
      cfg
    );
    return { data: result.data }; // Successful response
  } catch (e: any) {
    console.error(e);
    // Return a consistent error shape
    return {
      data: {
        data: [],
        meta: { current_page: pagination.page, last_page: pagination.page },
      },
    };
  }
};

export const apiGetRepSignedPetitions = async (
  pagination: PaginationParams
): Promise<{ data: APIResponse<ReportContainerPropsType> }> => {
  const { accessToken, tokenType } = await getAccessToken();
  const uri = `/accounts/petitions/signed?account_id=${pagination.account_id}&page=${pagination.page}&page_size=${pagination.page_size}`;
  const cfg = {
    headers: {
      Authorization: `${tokenType} ${accessToken}`,
    },
  };

  try {
    const result = await Axios.get<APIResponse<ReportContainerPropsType>>(
      uri,
      cfg
    );
    
    return { data: result.data }; // Successful response
  } catch (e: any) {
    console.error(e);
    // Return a consistent error shape
    return {
      data: {
        data: [],
        meta: { current_page: pagination.page, last_page: pagination.page },
      },
    };
  }
};
